import { useState } from 'react';
import { Calendar, Download, Loader2, ChevronDown, Lightbulb } from 'lucide-react';
import api from '../../services/api';

export default function TrainingPlan({ trainingPlan, analysisId }) {
  if (!trainingPlan || trainingPlan.length === 0) return null;

  return (
    <div className="card">
      <div className="flex items-center gap-3 mb-6">
        <Calendar className="w-5 h-5 text-bastion-blue-500" />
        <h2 className="text-lg font-semibold">7-Day Training Plan</h2>
      </div>

      <div className="relative">
        {/* Vertical timeline line */}
        <div className="absolute left-4 top-6 bottom-6 w-0.5 bg-bastion-gray-200" />

        <div className="space-y-4">
          {trainingPlan.map((day) => (
            <TrainingDay key={day.day} day={day} analysisId={analysisId} />
          ))}
        </div>
      </div>
    </div>
  );
}

function TrainingDay({ day, analysisId }) {
  const [expanded, setExpanded] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (e) => {
    e.stopPropagation();
    setDownloading(true);
    try {
      const response = await api.post(
        `/analysis/${analysisId}/training-worksheet/${day.day}`,
        {},
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `training-day-${day.day}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="relative pl-10">
      {/* Day circle */}
      <div className="absolute left-1.5 top-2 w-5 h-5 rounded-full bg-bastion-blue-500 text-white text-[10px] font-bold flex items-center justify-center z-10">
        {day.day}
      </div>

      <div className="bg-bastion-gray-50 rounded-lg p-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-start justify-between text-left"
        >
          <div>
            <h3 className="font-medium text-bastion-black">{day.title}</h3>
            <p className="text-sm text-bastion-gray-800 mt-0.5">{day.description}</p>
          </div>
          <ChevronDown className={`w-4 h-4 text-bastion-gray-400 mt-1 flex-shrink-0 ml-2 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>

        {expanded && (
          <div className="mt-3 space-y-3">
            {day.tips && day.tips.length > 0 && (
              <div className="space-y-1.5">
                {day.tips.map((tip, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <Lightbulb className="w-3.5 h-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span className="text-bastion-gray-800">{tip}</span>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={handleDownload}
              disabled={downloading}
              className="btn-outline text-sm py-2 px-4 w-full"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Download Day {day.day} Worksheet
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
